import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  DataService as CoreDataService,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
} from '@barthauer/abc/core/data';
import {
  AbcPopupComponent,
  Resource,
  _AbcFormComponent,
  _AbcGanttComponent,
  _AbcGridComponent,
} from '@barthauer/abc/ui-components';
import { Measure, MeasureTask } from '@barthauer/custom/xmt-models';
import { DxDataGridModule, DxGanttModule, DxPopupModule } from 'devextreme-angular';
import { exportGantt } from 'devextreme/pdf_exporter';
import { Column } from 'devextreme/ui/data_grid';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NotificationMessage, ROLES, ToastType } from '../../models/enumeration-list.model';
import { AppService } from '../../services/app.service';
import { DataService, ObjectKeys } from '../../services/data.service';
import { NotificationService } from '../../services/notification.service';
import { originOfCall } from '../../shared/milestone-task-popup/milestone-task-popup.service';
import { MilestoneComponent } from '../../shared/milestone-task-popup/milestone/milestone.component';
import { TaskComponent } from '../../shared/milestone-task-popup/task/task.component';
import { SharedModule } from '../../shared/shared.module';
import { MeasureService } from '../measure/measure.service';
import {
  GanttDateRange,
  GanttExportDocumentFormat,
  GanttExportMode,
  GanttTaskColor,
  IGanttDataSource,
  IResource,
  ResourceAssignment,
  ScaleType,
} from './gantt.model';
import { BasedRoleOPathService } from '../../services/based-role-opath.service';

@Component({
  selector: 'ubt-geods-gantt',
  templateUrl: './gantt.component.html',
  styleUrls: ['./gantt.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DxGanttModule,
    SharedModule,
    _AbcGanttComponent,
    _AbcFormComponent,
    AbcPopupComponent,
    DxPopupModule,
    DxDataGridModule,
    _AbcGridComponent,
    MilestoneComponent,
    TaskComponent,
  ],
})
export class GanttComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gantt') gantt: _AbcGanttComponent;
  @ViewChild('milestoneComponent') milestoneComponent: MilestoneComponent;
  @ViewChild('taskComponent') taskComponent: TaskComponent;
  @Output() onCalculateOverallTime: EventEmitter<number> = new EventEmitter();
  private taskVisibility$ = new BehaviorSubject<boolean>(false);
  public disableAddButton = false;
  public ganttDataSource: IGanttDataSource[] = [];
  public resources: IResource[] = [];
  public resourceAssignments: ResourceAssignment[] = [];
  public measureTask: MeasureTask = new MeasureTask(null);
  public createMeasureTask: MeasureTask = new MeasureTask(null);
  public collaboratorsColumns: Column[] = [];
  public tasksById = new Map<string, any>();
  public mileStoneById = new Map<string, MeasureTask>();
  public scaleType = ScaleType.Quarters;
  public ganttStartDate = new Date();
  public ROLES=ROLES
  public noDataText = this.dataService.res('Ubt-No-Data-text');
  private mutationObserver: MutationObserver | null = null;
  public exportOptions = {
    hint: 'Export to PDF',
    icon: 'custom-export-icon',
    stylingMode: 'text',
    onClick: () => this.exportToPDF(),
  };
  public hideTask: boolean = false;
  customButtonOptions = {
    text: 'Hide Aufgabe Tasks',
    onClick: () => this.toggleTasksVisibility(),
  };
  private measureTaskDs: IGanttDataSource[] = [];
  private subscriptions: Subscription[] = [];
  private ganttColumns = new QueryObjectsModel();
  private tasksIds: string[] = [];
  private measuresIds: string[] = [];
  private currentMeasure = new Measure(null);
  private measureParentId = '';

  constructor(
    public dataService: DataService,
    private appService: AppService,
    private coreDataService: CoreDataService,
    private measureService: MeasureService,
    private notificationService: NotificationService,
    private router: Router,
    private renderer: Renderer2,private  opathService:BasedRoleOPathService  ) {}

  ngAfterViewInit() {
    const ganttElement = this.gantt?.instance?.element();
    if (ganttElement) {
      this.subscriptions.push(
        this.taskVisibility$.subscribe((hideTask) => {
          if (hideTask) {
            this.initializeMutationObserver(ganttElement);
          } else {
            this.disconnectMutationObserver();
          }
        })
      );
    }
  }

  private initializeMutationObserver(ganttElement: HTMLElement) {
    this.mutationObserver = new MutationObserver(() => {
      if (this.hideTask) {
        this.hideAufgabeTasks();
      }
    });

    this.mutationObserver.observe(ganttElement, {
      childList: true,
      subtree: true,
    });
  }

  private disconnectMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
  public toggleTasksVisibility() {
    this.hideTask = !this.hideTask;
    this.taskVisibility$.next(this.hideTask);
    this.hideTask ? this.hideAufgabeTasks() : this.refreshTasks();
  }

  private hideAufgabeTasks() {
    const taskElements = this.gantt?.instance?.element()?.getElementsByClassName('dx-gantt-taskWrapper');
    if (taskElements) {
      Array.from(taskElements).forEach((taskGroup: Element) => {
        const indexValue = taskGroup.getAttribute('task-index');
        const taskIndex = Number(indexValue);

        if (!isNaN(taskIndex) && this.isTask(taskIndex)) {
          this.renderer.addClass(taskGroup, 'custom-task');
        }
      });
    }
  }

  private isTask(index: number): boolean {
    const task = this.ganttDataSource?.[index];
    return task && task.type === 'task';
  }

  private refreshTasks() {
    this.gantt.instance.zoomIn();
    this.gantt.instance.zoomOut();
  }
  public ngOnDestroy(): void {
    this.disconnectMutationObserver();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
  public ngOnInit(): void {
    if (this.dataService.cachedResponsiblesResponse.length === 0) {
      this.coreDataService
        .executeReadObjectsQuery(this.dataService.prepareStaffMembersWithDocuments())
        .subscribe((staffMembers) => {
          if (Array.isArray(staffMembers) && staffMembers.length > 0) {
            this.dataService.cachedResponsiblesResponse = staffMembers;
          }
        });
    }

    this.appService.showLoader(true);
    this.prepareGantt();
  }

  public exportToPDF() {
    exportGantt({
      component: this.gantt.instance,
      createDocumentMethod: (args?: any) => new jsPDF(args),
      format: GanttExportDocumentFormat.Auto,
      landscape: true,
      exportMode: GanttExportMode.All,
      dateRange: GanttDateRange.All,
    }).then((document) => document.save('gantt.pdf'));
  }
  public onCellClick(e: any) {
    let key = e.data;

    switch (key.type) {
      case 'subProject': {
        this.router.navigate(['subProjectDetail', key.Id]);
        break;
      }
      case 'measure': {
        this.appService.loadNotOnlyMeasureDetails(false);

        this.router.navigate(['measureDetail', 'measures', key.Id]);

        break;
      }
      case 'mileStone': {
        let measure = this.mileStoneById.get(key.Id);
        measure.Responsible = measure.IdRefResponsible;
        this.appService.loadNotOnlyMeasureDetails(true);
        this.appService.setMeasureTask(measure);
        this.router.navigate(['measureDetail', 'measures', measure.ParentId]);
        break;
      }
      case 'task': {
        let measure = this.tasksById.get(key.Id);
        measure.Collaborators = measure.Collaborators;
        measure.Responsible = measure.IdRefResponsible;
        this.appService.setMeasureTask(measure);
        this.appService.loadNotOnlyMeasureDetails(true);
        this.router.navigate(['measureDetail', 'measures', measure.ParentId]);
        break;
      }
    }
  }
  
    public CheckGantOpathForMeasures() :string{
      return this.opathService.CheckGantOpathForMeasures()
    }
    public checkGantOpathForSubProject():string {
      return this.opathService.checkGantOPathForSubProject()
    }
  public prepareGantt(): void {
    this.resources = [];
    this.resourceAssignments = [];
    this.ganttDataSource = [];
    const subProject: Query = new Query();
    const subProjectColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Designation', 'Label', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('SysDateInsert', 'SysDateInsert', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('SysDateUpdate', 'SysDateUpdate', QueryColumnSortOrder.None),
    ];
    subProject.ObjectType = ObjectKeys.SUBPROJECT;
    subProject.Columns = subProjectColumns;
    subProject.OPath =  this.checkGantOpathForSubProject();
    const measure: Query = new Query();
    measure.Name = 'xmtMeasure';
    measure.OPath = this.CheckGantOpathForMeasures();
    const measureColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
      this.coreDataService.createQueryColumn('Designation', 'Title'),
      this.coreDataService.createQueryColumn('StartDate', 'StartDate'),
      this.coreDataService.createQueryColumn('EndDate', 'EndDate'),
    ];
    measure.Columns = measureColumns;

    const measureTask: Query = new Query();
    measureTask.Name = 'xmtMeasureTask';
    measureTask.OPath = 'MeasureTasks';
    const measureTaskColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
      this.coreDataService.createQueryColumn('Subject', 'Label'),
      this.coreDataService.createQueryColumn('StartDate', 'StartDate'),
      this.coreDataService.createQueryColumn('EndDate', 'EndDate'),
      this.coreDataService.createQueryColumn('DegreeOfImplementation', 'DegreeOfImplementation'),
      this.coreDataService.createQueryColumn('TargetMaturityLevel', 'TargetMaturityLevel'),
      this.coreDataService.createQueryColumn('TotalTimeRequired', 'TotalTimeRequired'),
      this.coreDataService.createQueryColumn('Type', 'Type'),
      this.coreDataService.createQueryColumn('IdRefxmtMeasureTask', 'Task'),
      this.coreDataService.createQueryColumn('IdRefResponsible', 'IdRefResponsible'),
      this.coreDataService.createQueryColumn('Status', 'Status'),
      this.coreDataService.createQueryColumn('Comment', 'Comment'),
    ];
    measureTask.Columns = measureTaskColumns;

    const taskCollaborators: Query = new Query();
    taskCollaborators.Name = 'Collaborators';
    taskCollaborators.OPath = 'collaborators';
    const taskCollaboratorsColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
      this.coreDataService.createQueryColumn('TimeExecuted', 'TimeExecuted'),
      this.coreDataService.createQueryColumn('TimePlanned', 'TimePlanned'),
      this.coreDataService.createQueryColumn('IdRefMeasureResource', 'IdRefMember'),
    ];
    taskCollaborators.Columns = taskCollaboratorsColumns;

    const taskResources: Query = new Query();
    taskResources.Name = 'MeasureResource';
    taskResources.OPath = 'MeasureResource';
    const taskResourcesColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('IdRefPerson', 'Member'),
      this.coreDataService.createQueryColumn('ref:IdRefPerson', 'Person'),
    ];
    taskResources.Columns = taskResourcesColumns;
    taskCollaborators.ObjectQueries = [taskResources];

    measureTask.ObjectQueries = [taskCollaborators];
    measure.ObjectQueries = [measureTask];
    subProject.ObjectQueries = [measure];
    this.ganttColumns.ObjectQueries = [subProject];

    this.subscriptions.push(
      this.coreDataService.executeReadObjectsQuery(this.ganttColumns).subscribe(
        (data) => {
          if (Array.isArray(data) && data.length > 0) {
            data.forEach((subProject) => {
              const subProjectDs: IGanttDataSource = {
                Id: subProject.Id,
                Title: this.dataService.res('Ubt-Measure-List-SubProject') + ': ' + subProject.Label,
                ParentId: 0,
                StartDate: subProject.SysDateInsert,
                EndDate: subProject.SysDateUpdate,
                color: GanttTaskColor.Green,
                type: 'subProject',
              };
              this.ganttDataSource.push(subProjectDs);
              if (subProject.xmtMeasure?.length > 0) {
                subProject.xmtMeasure.forEach((measure) => {
                  this.measuresIds.push(measure.Id);
                  const measureDs: IGanttDataSource = {
                    Id: measure.Id,
                    ParentId: measure.ParentId,
                    Title: this.dataService.res('Ubt-CreateMeasureTask-Measure') + ': ' + measure.Title,
                    StartDate: measure.StartDate,
                    EndDate: measure.EndDate,
                    color: GanttTaskColor.LightGreen,
                    type: 'measure',
                  };
                  this.ganttDataSource.push(measureDs);
                  if (measure.xmtMeasureTask?.length > 0) {
                    measure.xmtMeasureTask.forEach((task) => {
                      const measureTask = new MeasureTask(task);

                      this.tasksIds.push(task.Id);

                      if (task.Type === 1 || task.Type === '1') {
                        this.tasksById.set(task.Id, measureTask);
                      } else {
                        this.mileStoneById.set(task.Id, measureTask);
                      }

                      const measureTaskDs: IGanttDataSource = {
                        Id: task.Id,
                        ParentId: task.ParentId,
                        Title:
                          (task.Type === 0
                            ? this.dataService.res('Ubt-MeasureDetail-Milestone')
                            : this.dataService.res('Ubt-MeasureDetail-Task')) +
                          ': ' +
                          task.Label,
                        StartDate: task?.StartDate,
                        EndDate: task.EndDate,
                        progress: task.DegreeOfImplementation,
                        color: GanttTaskColor.LightBlue,
                        type: task.Type === 1 ? 'task' : 'mileStone',
                        responsible: task.IdRefResponsible,
                      };
                      this.measureTaskDs.push(measureTaskDs);
                      this.ganttDataSource.push(measureTaskDs);
                      if (task.Collaborators?.length > 0) {
                        task.Collaborators.forEach((collaboratior) => {
                          if (collaboratior.MeasureResource?.length > 0) {
                            const collaboratorResource: IResource = {
                              id: collaboratior.Id,
                              text: collaboratior.MeasureResource[0].Person,
                            };
                            this.resources.push(collaboratorResource);
                          }

                          const assignments: ResourceAssignment = {
                            id: this.resourceAssignments.length != 0 ? this.resourceAssignments.length : 0,
                            taskId: task.Id,
                            resourceId: collaboratior.Id,
                          };
                          this.resourceAssignments.push(assignments);
                        });
                      }
                    });
                  }
                });
              }
            });
          } else {
            this.appService.showLoader(false);
            this.notificationService.callNotification({
              message: NotificationMessage.Error,
              type: ToastType.ERROR,
            });
          }
        },
        (error) => {
          console.error(error);
          this.appService.showLoader(false);
        },
        () => {
          this.ganttDataSource = [...new Map(this.ganttDataSource.map((v) => [v.Id, v])).values()];
          this.gantt.instance.repaint();
          this.gantt.instance.refresh();
          this.appService.showLoader(false);
        }
      )
    );
  }

  public contentReady = (e): void => {
    setTimeout(() => {
      const ganttStartDate = (this.ganttStartDate.getUTCFullYear() - 1).toString();
      this.gantt.instance.scrollToDate(ganttStartDate);
      this.gantt.instance.zoomIn();
    }, 0);
  };

  public contextMenuPreparing(event): void {
    if (event.data.ParentId !== '0' && this.measuresIds.find((item) => item === event.data.Id)) {
      this.disableAddButton = false;
      this.currentMeasure = event.data as Measure;
      this.measureParentId = event.data.Id;
    } else {
      this.disableAddButton = true;
    }
  }

  public createMilestone = (): void => {
    this.createMeasureTask = new MeasureTask(null);
    this.createMeasureTask.Label = '';
    this.milestoneComponent.showPopup(this.currentMeasure, this.createMeasureTask, false, originOfCall.Gantt);
  };

  public createTask = (): void => {
    this.createMeasureTask = new MeasureTask(null);
    this.createMeasureTask.Label = '';

    const resourceColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
      this.coreDataService.createQueryColumn('IdRefPerson', 'Member'),
      this.coreDataService.createQueryColumn('ref:IdRefPerson', 'Person'),
    ];

    const opath = 'ParentId=' + "'" + this.measureParentId + "'";
    this.subscriptions.push(
      this.dataService.readObjects<Resource[]>(ObjectKeys.RESOURCE, resourceColumns, opath).subscribe((data: any) => {
        this.collaboratorsColumns = [];
        this.measureService.collaboratorsColumns = [
          {
            caption: this.dataService.res('Ubt-CreateMeasure-Members'),
            dataField: 'IdRefMember',
            lookup: {
              valueExpr: 'Id',
              displayExpr: 'Person',
              dataSource: data?.length > 0 ? data.sort((a, b) => a.Person.localeCompare(b.Person)) : [],
            },
          },
          {
            caption: this.dataService.res('Ubt-Collaborator-Total-Time-Planned'),
            dataField: 'TimePlanned',
            dataType: 'number',
            format: '#,##0 h',
            alignment: 'center',
            width: '25%',
          },
          {
            caption: this.dataService.res('Ubt-Collaborator-Total-Time-Executed'),
            dataField: 'TimeExecuted',
            dataType: 'number',
            format: '#,##0 h',
            alignment: 'center',
            width: '25%',
          },
        ];
        this.measureService.isCreatingSubTask = true;
        this.taskComponent.showPopup(this.currentMeasure, this.createMeasureTask, false, originOfCall.Gantt);
      })
    );
  };

  public updateGantt(event: boolean) {
    if (event) this.prepareGantt();
  }
}
